// pages/Dashboard.jsx
import React, { useState, useEffect } from 'react';
import { useAuth, api } from '../../context/AuthContext';
import { FiKey, FiUsers, FiActivity, FiClock, FiTrendingUp, FiDollarSign, FiUserCheck, FiMapPin } from 'react-icons/fi';
import toast from 'react-hot-toast';

function Dashboard() {
  const { token, user } = useAuth();
  const [stats, setStats] = useState({
    totalKeys: 0,
    activeKeys: 0,
    totalRequests: 0,
    uptime: '0%',
  });
  const [systemStats, setSystemStats] = useState({
    totalUsers: 0,
    totalDrivers: 0,
    totalRides: 0,
    revenue: 0,
    sharePrice: 0,
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(new Date());

  useEffect(() => {
    if (token) {
      fetchAllData();
      const interval = setInterval(fetchAllData, 30000);
      return () => clearInterval(interval);
    }
  }, [token]);

  const fetchAllData = async () => {
    try {
      await Promise.all([
        fetchStats(),
        fetchSystemStats(),
        fetchRecentActivity(),
      ]);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/api/developer/stats');
      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchSystemStats = async () => {
    try {
      const response = await api.get('/api/analytics/daily');
      if (response.data.success) {
        const data = response.data.stats;
        setSystemStats({
          totalUsers: data.totalUsers || 0,
          totalDrivers: data.totalDrivers || 0,
          totalRides: data.totalRides || 0,
          revenue: data.todayRevenue || data.revenue || 0,
          sharePrice: data.sharePrice || 0,
        });
      }
    } catch (error) {
      console.error('Error fetching system stats:', error);
    }
  };

  const fetchRecentActivity = async () => {
    try {
      const response = await api.get('/api/rides?limit=5');
      if (response.data.success) {
        const rides = response.data.rides || [];
        const activities = rides.map(ride => ({
          id: ride.id,
          type: ride.status === 'completed' ? 'ride_completed' : 
                ride.status === 'pending' ? 'ride_requested' : 'ride_update',
          user: ride.passengerName || 'Unknown',
          time: new Date(ride.createdAt).toLocaleString(),
          details: `Ride ${ride.id?.slice(-6) || ''} - ${ride.status}`,
        }));
        setRecentActivity(activities);
      }
    } catch (error) {
      console.error('Error fetching recent activity:', error);
    }
  };

  if (loading) {
    return <div className="loading-container"><div className="spinner"></div><p>Loading dashboard...</p></div>;
  }

  const statsData = [
    { label: 'Total API Keys', value: stats.totalKeys, icon: <FiKey />, color: '#667eea' },
    { label: 'Active Keys', value: stats.activeKeys, icon: <FiActivity />, color: '#2ecc71' },
    { label: 'Total Requests', value: stats.totalRequests.toLocaleString(), icon: <FiClock />, color: '#ffa502' },
    { label: 'Uptime', value: stats.uptime, icon: <FiUsers />, color: '#ff4757' },
  ];

  const systemStatsData = [
    { label: 'Total Users', value: systemStats.totalUsers, icon: <FiUsers />, color: '#3498db' },
    { label: 'Total Drivers', value: systemStats.totalDrivers, icon: <FiUserCheck />, color: '#2ecc71' },
    { label: 'Total Rides', value: systemStats.totalRides.toLocaleString(), icon: <FiMapPin />, color: '#ffa502' },
    { label: 'Revenue', value: `$${systemStats.revenue.toFixed(2)}`, icon: <FiDollarSign />, color: '#667eea' },
    { label: 'Share Price', value: `$${systemStats.sharePrice.toFixed(2)}`, icon: <FiTrendingUp />, color: '#ff4757' },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
        <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
          Welcome back, {user?.fullName || user?.username}!
        </span>
        <button className="btn btn-outline" onClick={fetchAllData} style={{ padding: '4px 16px', fontSize: '12px' }}>
          🔄 Refresh
        </button>
      </div>

      <h3 style={{ marginBottom: '12px', color: 'var(--text-secondary)', fontSize: '14px' }}>📊 Developer Overview</h3>
      <div className="stats-grid">
        {statsData.map((stat, index) => (
          <div className="stat-card" key={index}>
            <div className="stat-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ color: stat.color }}>{stat.icon}</span>
              {stat.label}
            </div>
            <div className="stat-value">{stat.value}</div>
          </div>
        ))}
      </div>

      <h3 style={{ marginBottom: '12px', color: 'var(--text-secondary)', fontSize: '14px' }}>🏢 System Overview</h3>
      <div className="stats-grid">
        {systemStatsData.map((stat, index) => (
          <div className="stat-card" key={index}>
            <div className="stat-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ color: stat.color }}>{stat.icon}</span>
              {stat.label}
            </div>
            <div className="stat-value">{stat.value}</div>
          </div>
        ))}
      </div>

      <div className="card">
        <div className="card-header">
          <span className="card-title">🚀 Quick Actions</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
          <button className="btn btn-primary" onClick={() => window.location.href = '/api-keys'}>
            <FiKey /> Generate API Key
          </button>
          <button className="btn btn-outline" onClick={() => window.location.href = '/api-docs'}>
            📖 View Documentation
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <span className="card-title">📋 Recent Activity</span>
          <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
            {recentActivity.length} activities
          </span>
        </div>
        {recentActivity.length === 0 ? (
          <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '20px' }}>
            No recent activity found.
          </div>
        ) : (
          <div>
            {recentActivity.map(activity => (
              <div key={activity.id} style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '12px', 
                padding: '10px 0',
                borderBottom: '1px solid var(--border-color)',
              }}>
                <span style={{ fontSize: '20px' }}>{activity.type === 'ride_completed' ? '🚕' : '📌'}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '500' }}>{activity.details}</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                    {activity.user} • {activity.time}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default Dashboard;